import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import classNames from "classnames";
import format from "date-fns/format";
import isFuture from "date-fns/isFuture";
import parseJSON from "date-fns/parseJSON";
import { truncate } from "lodash";
import { twMerge } from "tailwind-merge";

import { faFile } from "@fortawesome/free-regular-svg-icons";
import {
  faChevronDown,
  faChevronUp,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons/faExternalLinkAlt";

import AddToCalendarButton from "components/AddToCalendarButton";
import DiscussionComposeModal from "components/DiscussionComposeModal";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useNotification from "components/Notification/useNotification";
import RichContent from "components/RichContent";
import ShareButton from "components/ShareButton";
import useActivityTracker from "hooks/useActivityTracker";
import useMediaQuery from "hooks/useMediaQuery";
import { fetchApi } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";
import htmlText from "utils/htmlToText";

import styles from "./style.module.scss";
import SummariseButton from "./SummariseButton";

const SUMMARIZATION_STORAGE_KEY = "sk_summarization_progress";

const getSummarizationEntry = (resourceId: string) => {
  try {
    const data = JSON.parse(
      localStorage.getItem(SUMMARIZATION_STORAGE_KEY) || "{}"
    );
    return data[resourceId] ?? null;
  } catch {
    return null;
  }
};

const saveSummarizationEntry = (resourceId: string) => {
  try {
    const data = JSON.parse(
      localStorage.getItem(SUMMARIZATION_STORAGE_KEY) || "{}"
    );
    data[resourceId] = { startTime: Date.now() };
    localStorage.setItem(SUMMARIZATION_STORAGE_KEY, JSON.stringify(data));
  } catch {}
};

const clearSummarizationEntry = (resourceId: string) => {
  try {
    const data = JSON.parse(
      localStorage.getItem(SUMMARIZATION_STORAGE_KEY) || "{}"
    );
    delete data[resourceId];
    localStorage.setItem(SUMMARIZATION_STORAGE_KEY, JSON.stringify(data));
  } catch {}
};

const AudioPlayer = dynamic(() => import("components/AudioPlayer"), {
  ssr: false,
});

const normalizeUrl = (url: string) =>
  url?.startsWith("https://") ? url : `https://${url}`;

const TYPE_META: Record<string, { source: string; tab?: string }> = {
  "corporate-events": { source: "Company Event", tab: "events" },
  "corporate-presentations": {
    source: "Company Presentation",
    tab: "presentations",
  },
  "entity-earnings-alerts": { source: "Earnings", tab: "earnings-alerts" },
  "exchange-announcements": { source: "Exchange Announcement" },
  "entity-news": { source: "News" },
};

const AnnoucementSnippet = ({
  item,
  useModal,
  isLatest,
  isPendingSummarization,
  onViewSummary,
  onStartSummarization,
  shouldHideSummarizeButton,
}: any) => {
  const { setNotification } = useNotification();
  const router = useRouter();
  const { mobile } = useMediaQuery();
  const { trackActivity } = useActivityTracker();

  const [showAccordionDetails, setShowAccordionDetails] = useState(false);
  const [loadingUrl, setLoadingUrl] = useState<string | null>(null);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [composeDefaultValue, setComposeDefaultValue] = useState<string>("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const loadingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const startProgressInterval = (initialProgress: number) => {
    setLoadingProgress(initialProgress);
    loadingIntervalRef.current = setInterval(() => {
      setLoadingProgress((prev) => Math.min(prev + 15, 95));
    }, 1000);
  };

  useEffect(() => {
    const resourceId = String(item.id);
    const stored = getSummarizationEntry(resourceId);
    if (stored && !item.summarizations?.length) {
      const elapsedSeconds = (Date.now() - stored.startTime) / 1000;
      const restoredProgress = Math.min(Math.floor(elapsedSeconds * 15), 95);
      startProgressInterval(restoredProgress);
      setLoadingUrl("__external__");
      onStartSummarization?.(resourceId);
    }
    return () => {
      if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPendingSummarization && loadingUrl === null) {
      saveSummarizationEntry(String(item.id));
      startProgressInterval(0);
      setLoadingUrl("__external__");
    } else if (!isPendingSummarization && loadingUrl !== null) {
      if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
      clearSummarizationEntry(String(item.id));
      setLoadingUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPendingSummarization]);

  const text = item.title;
  const firstUrl =
    item.attachment?.[0] || item.corporateAttachments?.[0]?.source;
  const releaseTime =
    item.releaseTime || item.publishedAt || item.startAt || item.createdAt;

  const isSingleUrl = useMemo(() => {
    if (item.type === "entity-news") {
      return (
        (item?.attachment?.length || 0) +
          (item?.corporateAttachments?.length || 0) ===
        0
      );
    }

    return (
      (item?.attachment?.length || 0) +
        (item?.corporateAttachments?.length || 0) ===
      1
    );
  }, [item?.attachment?.length, item?.corporateAttachments?.length, item.type]);

  const url = useMemo(() => {
    if (item.type === "entity-news") return normalizeUrl(item.url);
    const meta = TYPE_META[item.type];
    if (!meta) return "";
    const tabParam = meta.tab ? `tab=${meta.tab}&` : "";
    return `/entities/${router.query.slug}/announcements?${tabParam}item=${item.id}`;
  }, [item?.id, item?.type, item?.url, router?.query?.slug]);

  const shareUrl = useMemo(() => {
    if (item.type === "corporate-events") {
      return `${emberAppBaseUrl}/entities/${router.query.slug}/announcements?tab=events&item=${item.id}`;
    }
    if (item.type === "corporate-presentations") {
      return `${emberAppBaseUrl}/entities/${router.query.slug}/announcements?tab=presentations&item=${item.id}`;
    }
    if (item.type === "entity-earnings-alerts") {
      return `${emberAppBaseUrl}/entities/${router.query.slug}/announcements?tab=earnings-alerts&item=${item.id}`;
    }
    if (item.type === "exchange-announcements") {
      return `${emberAppBaseUrl}/entities/${router.query.slug}/announcements?item=${item.id}`;
    }
    if (item.type === "entity-news") {
      return item.url.startsWith("https://") ? item.url : `https://${item.url}`;
    }
    return "";
  }, [item?.id, item?.type, item?.url, router?.query?.slug]);

  const type = useMemo(() => {
    if (item.type === "corporate-events") {
      return "Event";
    }
    if (item.type === "corporate-presentations") {
      return "Presentation";
    }
    if (item.type === "exchange-announcements") {
      return "Filing";
    }
    if (item.type === "entity-earnings-alerts") {
      return "Earnings";
    }
    if (item.type === "entity-news") {
      return "News";
    }
    return "";
  }, [item.type]);

  const shareBtnOptions = useCallback(
    (link) => {
      const shareLink = link || shareUrl;
      const clipboardText = `${item?.entity?.shortName} | ${item?.title}: ${shareLink}`;
      const whatsappText = `Check this out ${clipboardText}`;
      const twitterText = truncate(item?.title, { length: 100 });
      return {
        email: {
          link: `mailto:?subject=✪ Smartkarma | ${type}: | ${item?.title}&body=${whatsappText}`,
        },
        clipboard: {
          message: shareLink,
        },
        privateMessage: {
          message: `Check this out <a href="${shareLink}">${item?.entity?.shortName} | ${item?.title}</a>`,
        },
        whatsapp: {
          link: `https://web.whatsapp.com/send?text=${whatsappText}`,
        },
        twitter: {
          link: `https://twitter.com/share?url=${shareLink}&text=${twitterText}&via=smartkarma`,
        },
        linkedIn: {
          link: `https://www.linkedin.com/shareArticle?mini=true&url=${shareLink}&source=Smartkarma`,
        },
      };
    },
    [item?.entity?.shortName, item?.title, shareUrl, type]
  );

  const isUpcomingQuarterEvent = useMemo(() => {
    return (
      item.type === "corporate-events" &&
      item.source === "quartr" &&
      (isFuture(parseJSON(releaseTime)) || !item.sourceUrl)
    );
  }, [item.source, item.sourceUrl, item.type, releaseTime]);

  const toggleDetails = useCallback(
    (event: React.MouseEvent, forceModal?: boolean) => {
      try {
        switch (item.type) {
          case "corporate-events":
            trackActivity("corporate_event.view", item);
            break;
          case "corporate-presentations":
            trackActivity("corporate_presentation.view", item);
            break;
          case "exchange-announcements":
            trackActivity("exchange_announcement.view", item);
            break;
          case "entity-news":
            trackActivity("entity_news.view", item);
            break;
        }
      } catch (err) {
        console.debug(err);
      }

      if (useModal || forceModal) {
        if (
          item.type === "corporate-events" ||
          item.type === "corporate-presentations" ||
          item.type === "entity-earnings-alerts"
        ) {
          event.preventDefault();
          return router.push(url, undefined, { shallow: true });
        }
        return;
      }

      if (item.type === "entity-news" && isSingleUrl) {
        window.open(item.url, "_blank");
        return;
      }

      if (!isUpcomingQuarterEvent) {
        setShowAccordionDetails(!showAccordionDetails);
      }
    },
    [
      useModal,
      item,
      isSingleUrl,
      isUpcomingQuarterEvent,
      trackActivity,
      router,
      url,
      showAccordionDetails,
    ]
  );

  const showDetail = useMemo(() => {
    if (item.source !== "quartr") return true;

    return !!item.sourceUrl;
  }, [item.source, item.sourceUrl]);

  const source = TYPE_META[item.type]?.source ?? "";

  const onSummarise = useCallback(
    async (sourceUrl: string) => {
      if (!isSingleUrl && !showAccordionDetails) {
        setShowAccordionDetails(!showAccordionDetails);
        return;
      }

      if (
        item.summarizations.length &&
        item.summarizations
          .map((item: any) => item.sourceUrl)
          .includes(sourceUrl)
      ) {
        const summary = item.summarizations.find(
          (summarization: any) => summarization.sourceUrl == sourceUrl
        );
        const content = summary?.content;
        if (content.documentTitle === "N/A") {
          setNotification(
            "We are unable to summarise this content.\nClick on the Attachment to access the underlying filing.",
            "error"
          );
          return;
        }

        onViewSummary?.(item, {
          ...content,
          sourceUrl,
        });
        return;
      }

      saveSummarizationEntry(String(item.id));
      setLoadingUrl(sourceUrl);
      startProgressInterval(0);
      onStartSummarization?.(item.id);

      try {
        await fetchApi("/api/v3/summarize", {
          method: "POST",
          body: JSON.stringify({
            resource_type: "ExchangeAnnouncement",
            resource_id: item.id,
            source_url: sourceUrl,
          }),
        });
      } catch (err) {
        console.debug(err);
      }
    },
    [
      isSingleUrl,
      showAccordionDetails,
      item,
      onStartSummarization,
      onViewSummary,
      setNotification,
    ]
  );

  const allowSummarizationButton = useCallback(
    (url) => {
      const entity = item.entity;
      const security = entity.security.toLowerCase();
      const isPDF = url.includes(".pdf");

      if (item.type !== "exchange-announcements") return false;
      if (/(sp equity|jp equity)/.test(security ?? "") && !isPDF) return false;
      if (url.startsWith("https://static-prod.smartkarma.com/")) return true;

      return item.entity.supportSummarization && !shouldHideSummarizeButton;
    },
    [item.entity, item.type, shouldHideSummarizeButton]
  );

  const ctasButton = useCallback(
    (url, isCorporateAttachment = false) => {
      const allowSummarization =
        allowSummarizationButton(url) && !isCorporateAttachment;
      return (
        <div className="flex items-center md:justify-center gap-[5px]">
          <OverlayTrigger
            overlay={
              <Tooltip>
                {allowSummarization
                  ? "Summarize"
                  : "We summarise most filings but this one isn't supported yet"}
              </Tooltip>
            }
          >
            <div className="">
              <SummariseButton
                url={url}
                allowSummarization={allowSummarization}
                onSummarise={onSummarise}
                isLoading={loadingUrl === url}
                hasSummary={!!item?.summarizations?.length}
              />
            </div>
          </OverlayTrigger>
          <OverlayTrigger overlay={<Tooltip>Post a discussion</Tooltip>}>
            <div
              className="flex items-center justify-center w-[40px] h-[40px] rounded-full hover:bg-smart-teal/10 transition-all"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setComposeDefaultValue(
                  url ? `<p><a href="${url}">${url}</a></p>` : ""
                );
                setShowComposeModal(true);
              }}
            >
              <FontAwesomeIcon className="" icon={faPaperPlane} />
            </div>
          </OverlayTrigger>
          <ShareButton
            shareKey={`entity-${item.type}-${item.id}`}
            containerClassName="flex items-center justify-center w-[40px] h-[40px] rounded-full hover:bg-smart-teal/10 transition-all"
            activeClassName="!block"
            options={shareBtnOptions(url)}
            columnView
            noBorder
            tooltipPlacement="top"
          ></ShareButton>
        </div>
      );
    },
    [
      item.id,
      item?.summarizations?.length,
      item.type,
      loadingUrl,
      onSummarise,
      shareBtnOptions,
      allowSummarizationButton,
    ]
  );

  const renderSummarizationLoading = useCallback(
    (url) => {
      if (loadingUrl === null) return;
      if (loadingUrl !== url) return null;

      return (
        <div className="mt-[10px]">
          <div className="w-full bg-gray-200 rounded-full h-[3px] overflow-hidden">
            <div
              className="bg-smart-teal h-full rounded-full transition-all duration-700"
              style={{ width: `${loadingProgress}%` }}
            />
          </div>
          <div className="flex justify-between items-center mt-[5px]">
            <span className="flex items-center gap-[6px] text-[12px] text-intelligent-silver">
              <span className="w-[8px] h-[8px] rounded-full bg-smart-teal inline-block" />
              Smartkarma AI · Reading filing &amp; generating summary
            </span>
            <span className="text-[12px] text-intelligent-silver">
              {loadingProgress}%
            </span>
          </div>
        </div>
      );
    },
    [loadingProgress, loadingUrl]
  );

  const renderIcon = useMemo(() => {
    return (
      <div className="w-[100px] border-0 border-r border-solid border-[#ddd] my-[15px] flex flex-col gap-[10px] items-center justify-center">
        <div className="w-[60px] h-[60px] bg-smart-teal/10 rounded-full flex items-center justify-center">
          <FontAwesomeIcon
            icon={faFile}
            className="text-smart-teal text-[20px]"
          />
        </div>
        <span className="font-medium text-[15px]">FILING</span>
      </div>
    );
  }, []);

  const attachments = useMemo(() => {
    return (
      <div className="">
        {item?.attachment?.map?.((url: string, index: number) => {
          const isPDF = url.includes(".pdf");

          return (
            <div
              key={url}
              className={twMerge(
                "flex justify-between group mt-[10px] cursor-pointer",
                "border-solid border border-light-gray rounded-[10px]"
              )}
              onClick={() =>
                window.open(
                  url,
                  "MyWindow",
                  "width=screen.availWidth,height=screen.availHeight"
                )
              }
            >
              {renderIcon}
              <div className="flex flex-col flex-1">
                <div className="flex flex-row md:items-center md:justify-between flex-wrap gap-[15px] flex-1 p-[15px]">
                  <div className="flex-1 justify-center flex flex-col min-w-[105px]">
                    <a className="font-medium text-smart-teal">
                      Announcement {isSingleUrl ? "" : index + 1}
                    </a>
                    <span className="text-inactive-gray text-[13px]">
                      {isPDF ? "PDF" : "WEB"}
                    </span>

                    {!mobile && renderSummarizationLoading(url)}
                  </div>
                  {ctasButton(url)}
                </div>
                {mobile && (
                  <div className="mx-[15px]">
                    {renderSummarizationLoading(url)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {item?.corporateAttachments?.map?.((attachment: any, index: number) => {
          const isPDF = attachment.source.includes(".pdf");
          return (
            <div
              key={attachment.source}
              className={twMerge(
                "flex justify-between group mt-[10px] cursor-pointer",
                "border-solid border border-light-gray rounded-[10px]"
              )}
              onClick={(event) => {
                if (item.type === "corporate-presentations") {
                  toggleDetails(event, true);
                  return;
                }
                window.open(
                  attachment.source,
                  "MyWindow",
                  "width=screen.availWidth,height=screen.availHeight"
                );
              }}
            >
              {renderIcon}
              <div className="flex items-center justify-between flex-1 p-[15px]">
                <div className="flex flex-col flex-1">
                  <a className="flex-1 font-medium text-smart-teal">
                    Announcement {isSingleUrl ? "" : index + 1}
                  </a>
                  <span className="text-inactive-gray text-[13px]">
                    {isPDF ? "PDF" : "Webpage"}
                  </span>
                </div>
                {ctasButton(attachment.source, true)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [
    ctasButton,
    isSingleUrl,
    item?.attachment,
    item?.corporateAttachments,
    item.type,
    mobile,
    renderIcon,
    renderSummarizationLoading,
    toggleDetails,
  ]);

  return (
    <div
      className={classNames(styles.container, mobile && styles.mobileLayout)}
    >
      <div
        className="flex items-center justify-between group"
        onClick={toggleDetails}
      >
        <div className="flex-1 cursor-pointer group/title">
          {releaseTime && (
            <div className="flex items-center space-x-5 cursor-pointer">
              <div
                className={twMerge(styles.time, "flex-1")}
                suppressHydrationWarning
              >
                <span>{source} • </span>
                {format(parseJSON(releaseTime), "dd MMM yyyy HH:mm")}
                {isUpcomingQuarterEvent && (
                  <>
                    {" "}
                    • Upcoming{"  "}
                    <AddToCalendarButton
                      startTime={parseJSON(releaseTime)}
                      endTime={parseJSON(releaseTime)}
                      location={item.location}
                      name={text}
                      details={htmlText(item.detail || item.content)}
                      onAddToCalendar={(type) => {
                        trackActivity("corporate_event.add_to_calendar", item, {
                          calendar: type,
                        });
                      }}
                    />
                  </>
                )}
                {isLatest && " • Latest"}
              </div>
            </div>
          )}
          <div
            className={twMerge(
              styles.title,
              "flex items-center justify-between flex-1 !text-[18px] mt-[5px]",
              "group-hover/title:text-smart-teal"
            )}
          >
            <h3 className="text-[18px] line-clamp-2 mb-0 flex-1">{text}</h3>
            {item.type !== "entity-news" && (
              <div className="flex items-center justify-center w-[40px] h-[40px] rounded-full">
                <FontAwesomeIcon
                  className="text-shady-gray"
                  icon={showAccordionDetails ? faChevronUp : faChevronDown}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className={twMerge(
          "px-0 transition-all duration-500",
          showAccordionDetails ? "" : "hidden"
        )}
      >
        {item.type === "entity-news" && item.url && (
          <Link
            href={normalizeUrl(item.url)}
            className="text-smart-teal group"
            target="_blank"
          >
            Link
            <FontAwesomeIcon
              icon={faExternalLinkAlt}
              className="ml-[5px] hidden group-hover:inline"
            />
          </Link>
        )}
        {item.type === "corporate-events" && (
          <>
            <div className={""}>
              <div className={""}>
                {item.location && (
                  <div className={"text-[#555] mb-[10px]"}>
                    <strong>
                      <span>Location:</span>
                    </strong>
                    {item.location}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        {showDetail && (
          <div className="relative">
            {item.source === "quartr" ? (
              <div className="mt-[15px]">
                <div className="mb-[10px]">
                  <AudioPlayer url={item?.sourceUrl} title={item?.title} />
                </div>
                {item.quartrEventUrls?.reportUrl && (
                  <a
                    className="block text-smart-teal group cursor-pointer"
                    href="#"
                    onClick={() =>
                      window.open(
                        item.quartrEventUrls?.reportUrl,
                        "MyWindow",
                        "width=screen.availWidth,height=screen.availHeight"
                      )
                    }
                  >
                    Company Announcement
                    <FontAwesomeIcon
                      icon={faExternalLinkAlt}
                      className="ml-[5px] hidden group-hover:inline"
                    />
                  </a>
                )}
                {item.quartrEventUrls?.pdfUrl &&
                  item.quartrEventUrls?.pdfUrl !==
                    item.quartrEventUrls?.reportUrl && (
                    <a
                      className="block text-smart-teal group cursor-pointer"
                      onClick={() =>
                        window.open(
                          item.quartrEventUrls?.pdfUrl,
                          "MyWindow",
                          "width=screen.availWidth,height=screen.availHeight"
                        )
                      }
                    >
                      Company Presentation
                      <FontAwesomeIcon
                        icon={faExternalLinkAlt}
                        className="ml-[5px] hidden group-hover:inline"
                      />
                    </a>
                  )}
              </div>
            ) : (
              <RichContent
                html={item.detail || item.bodyListicle}
                className={`${item.type}-${item.source}-detail`}
              />
            )}
          </div>
        )}
        {attachments}
      </div>

      {showComposeModal && (
        <DiscussionComposeModal
          style="v3"
          commentable={item?.entity}
          defaultValue={composeDefaultValue}
          onClose={() => setShowComposeModal(false)}
          onDiscussionPost={(discussion) => {
            if (discussion) {
              setNotification("Discussion Posted!", "success");
            }
            setShowComposeModal(false);
          }}
        />
      )}
    </div>
  );
};

export default AnnoucementSnippet;
